/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-underscore-dangle */
import { EventContentArg } from '@fullcalendar/react';
import { CalendarView, FlightCategory, MonthViewEvents, RequestStatus, TARStatus } from 'src/constants';
import Lang from 'src/libraries/language';
import Moment from 'src/libraries/moment.library';
import { IFlightsCalendarEvents, IFlightsCalendarMonthView } from 'src/models/flights-calendar.model';
import { IRequestItem } from 'src/models/request.model';

const getCategoryColor = (
  flightCategoryKey: number | null | undefined,
  alpha: number
): string => {
  switch (flightCategoryKey) {
    case FlightCategory.ConstructionWork: {
      return `rgba(250, 128, 114, ${alpha})`;
    }
    case FlightCategory.DroneFlightInternal: {
      return `rgba(166, 213, 168, ${alpha})`;
    }
    case FlightCategory.PatrolPassenger: {
      return `rgba(255, 242, 205, ${alpha})`;
    }
    case FlightCategory.DroneFlightExternal: {
      return `rgba(217, 185, 250, ${alpha})`;
    }
    default: {
      return `rgba(168, 97, 237, ${alpha})`;
    }
  }
};

const getCategoryBgColor = (
  flightCategory: string,
  flightDate?: Date | null | undefined,
  requestStatus?: string | null | undefined,
  ): string => {
  let alpha = 1;
  if (
    (flightDate && flightDate < new Date()) ||
    (requestStatus && (
      requestStatus === RequestStatus.Completed ||
      requestStatus === RequestStatus.RequiresFlightCost
    ))
  ) {
    alpha = 0.5;
  }
  switch (flightCategory) {
    case MonthViewEvents.Construction: {
      return `rgba(250, 128, 114, ${alpha})`;
    }
    case MonthViewEvents.InternalDrone: {
      return `rgba(166, 213, 168, ${alpha})`;
    }
    case MonthViewEvents.PatrolPassenger: {
      return `rgba(255, 242, 205, ${alpha})`;
    }
    case MonthViewEvents.ExternalManned: {
      return `rgba(168, 97, 237, ${alpha})`;
    }
    case MonthViewEvents.ExternalDrone: {
      return `rgba(217, 185, 250, ${alpha})`;
    }
    default: {
      return `rgba(198, 198, 198, ${alpha})`;
    }
  }
};

const getColor = (
  flightCategoryKey: number | null | undefined,
  flightStatus: string | null | undefined,
  alpha: number
): string => {
  if (flightStatus === RequestStatus.PendingApproval) {
    return `rgba(198, 198, 198, ${alpha})`;
  }
  if (flightStatus === RequestStatus.Cancelled ||
    flightStatus === RequestStatus.Declined) {
      return `rgba(255, 255, 255, ${alpha})`;
  }
  return getCategoryColor(flightCategoryKey, alpha);
};

const getIcon = (flightCategoryKey: number | null | undefined): string => {
  switch (flightCategoryKey) {
    case FlightCategory.PatrolPassenger: {
      return 'fa-regular fa-helicopter';
    }
    case FlightCategory.ContractorPatrol: {
      return 'fa-regular fa-helicopter';
    }
    case FlightCategory.ConstructionWork: {
      return 'fa-regular fa-screwdriver-wrench';
    }
    case FlightCategory.ContractorConstruction: {
      return 'fa-regular fa-screwdriver-wrench';
    }
    case FlightCategory.DroneFlightInternal: {
      return 'fa-regular fa-drone-front';
    }
    case FlightCategory.DroneFlightExternal: {
      return 'fa-regular fa-drone-front';
    }
    default: return '';
  }
};

const getTar = (tarStatus: string | null | undefined): string => {
  switch (Number(tarStatus)) {
    case TARStatus.Approved: {
      return 'fa-solid fa-star';
    }
    case TARStatus.Denied: {
      return 'fa-solid fa-star-half-stroke';
    }
    case TARStatus.Submitted: {
      return 'fa-solid fa-star-half-stroke';
    }
    default: return 'fa-regular fa-star';
  }
};

const getFlights = (list: IRequestItem[], category: number, isPending: boolean): IRequestItem[] => {
  return list.filter((item) =>
    item.flightCategoryKey === category && (
      isPending ?
      (item.requestStatus === RequestStatus.PendingApproval || item.requestStatus === RequestStatus.Received) :
      (item.requestStatus !== RequestStatus.PendingApproval && item.requestStatus !== RequestStatus.Received)
    )
  );
};

export const getMappedMonthViewEvents = (list: IRequestItem[]): IFlightsCalendarMonthView[] => {
  const patrolPassenger = getFlights(list, FlightCategory.PatrolPassenger, false);
  const construction = getFlights(list, FlightCategory.ConstructionWork, false);
  const drone = getFlights(list, FlightCategory.DroneFlightInternal, false);

  const externalManned = Array.prototype.concat(
    getFlights(list, FlightCategory.ContractorConstruction, false),
    getFlights(list, FlightCategory.ContractorPatrol, false)
  ) as IRequestItem[];
  const externalDrone = getFlights(list, FlightCategory.DroneFlightExternal, false);

  const pending = [
    ...getFlights(list, FlightCategory.PatrolPassenger, true),
    ...getFlights(list, FlightCategory.ConstructionWork, true),
    ...getFlights(list, FlightCategory.DroneFlightInternal, true),
    ...getFlights(list, FlightCategory.DroneFlightExternal, true),
    ...Array.prototype.concat(
      getFlights(list, FlightCategory.ContractorConstruction, true),
      getFlights(list, FlightCategory.ContractorPatrol, true)
    ) as IRequestItem[],
  ];

  const getCount = (category: string, flightDate: Date | null | undefined): number => {
    switch (category) {
      case MonthViewEvents.Construction: {
        return construction.filter((item) => String(item.flightDate) === String(flightDate)).length;
      }
      case MonthViewEvents.InternalDrone: {
        return drone.filter((item) => String(item.flightDate) === String(flightDate)).length;
      }
      case MonthViewEvents.PatrolPassenger: {
        return patrolPassenger.filter((item) => String(item.flightDate) === String(flightDate)).length;
      }
      case MonthViewEvents.ExternalManned: {
        return externalManned.filter((item) => String(item.flightDate) === String(flightDate)).length;
      }
      case MonthViewEvents.ExternalDrone: {
        return externalDrone.filter((item) => String(item.flightDate) === String(flightDate)).length;
      }
      default: {
        return 0;
      }
    }
  };

  const getTARApproved = (category: string, flightDate: Date | null | undefined): string => {
    switch (category) {
      case MonthViewEvents.Construction: {
        const approved = construction
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => Number(item.tarStatus) === TARStatus.Approved);
        return approved && approved !== undefined ? 'fa-solid fa-star' : '';
      }
      case MonthViewEvents.InternalDrone: {
        const approved = drone
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => Number(item.tarStatus) === TARStatus.Approved);
        return approved && approved !== undefined ? 'fa-solid fa-star' : '';
      }
      case MonthViewEvents.PatrolPassenger: {
        const approved = patrolPassenger
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => Number(item.tarStatus) === TARStatus.Approved);
        return approved && approved !== undefined ? 'fa-solid fa-star' : '';
      }
      case MonthViewEvents.ExternalManned: {
        const approved = externalManned
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => Number(item.tarStatus) === TARStatus.Approved);
        return approved && approved !== undefined ? 'fa-solid fa-star' : '';
      }
      case MonthViewEvents.ExternalDrone: {
        const approved = externalDrone
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => Number(item.tarStatus) === TARStatus.Approved);
        return approved && approved !== undefined ? 'fa-solid fa-star' : '';
      }
      default: {
        return '';
      }
    }
  };

  const getHEC = (category: string, flightDate: Date | null | undefined): string => {
    switch (category) {
      case MonthViewEvents.Construction: {
        const hec = construction
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => item.flightType && item.flightType?.includes('HEC'));
        return hec && hec !== undefined ? 'fa-solid fa-male' : '';
      }
      case MonthViewEvents.InternalDrone: {
        const hec = drone
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => item.flightType && item.flightType?.includes('HEC'));
        return hec && hec !== undefined ? 'fa-solid fa-male' : '';
      }
      case MonthViewEvents.PatrolPassenger: {
        const hec = patrolPassenger
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => item.flightType && item.flightType?.includes('HEC'));
        return hec && hec !== undefined ? 'fa-solid fa-male' : '';
      }
      case MonthViewEvents.ExternalManned: {
        const hec = externalManned
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => item.flightType && item.flightType?.includes('HEC'));
        return hec && hec !== undefined ? 'fa-solid fa-male' : '';
      }
      case MonthViewEvents.ExternalDrone: {
        const hec = externalDrone
          .filter((item) => String(item.flightDate) === String(flightDate))
          .find((item) => item.flightType && item.flightType?.includes('HEC'));
        return hec && hec !== undefined ? 'fa-solid fa-male' : '';
      }
      default: {
        return '';
      }
    }
  };

  const createMVEvents = (array: IRequestItem[], category: string): IFlightsCalendarMonthView[] => {
    const arr = array.map((item) => {
      let pendingCount;
      const alpha = (item.flightDate && item.flightDate < new Date()) ||
      (item.requestStatus && (
        item.requestStatus === RequestStatus.Completed ||
        item.requestStatus === RequestStatus.RequiresFlightCost
      )) ? 0.5 : 1;
      if (category === MonthViewEvents.PendingApproval || category === MonthViewEvents.Received) {
        const getLength = (flightCategoryKey) => array.filter((data) =>
          String(data.flightDate) === String(item.flightDate) &&
          flightCategoryKey === data.flightCategoryKey
        ).length;
        pendingCount = {
          construction: getLength(FlightCategory.ConstructionWork),
          externalManned: (
            getLength(FlightCategory.ContractorConstruction) +
            getLength(FlightCategory.ContractorPatrol)
          ),
          externalDrone: (
            getLength(FlightCategory.DroneFlightExternal)
          ),
          patrolPassenger: getLength(FlightCategory.PatrolPassenger),
          drone: getLength(FlightCategory.DroneFlightInternal),
        };
      }
      return {
        title: category,
        start: item.flightDate,
        backgroundColor: getCategoryBgColor(category, item.flightDate, item.requestStatus),
        borderColor: 'black',
        textColor: `rgba(10, 10, 10, ${alpha})`,
        count: getCount(category, item.flightDate),
        pendingCount,
        tarIcon: getTARApproved(category, item.flightDate),
        hecIcon: getHEC(category, item.flightDate),
      };
    });
    return Array.from(
      new Map(arr.map((item) => [String(Moment(item.start).startOf('day').toDate()), item])).values()
    ) as IFlightsCalendarMonthView[];
  };

  const ppArray = createMVEvents(patrolPassenger, MonthViewEvents.PatrolPassenger);
  const conArray = createMVEvents(construction, MonthViewEvents.Construction);
  const droneArray = createMVEvents(drone, MonthViewEvents.InternalDrone);
  const exMannedArray = createMVEvents(externalManned, MonthViewEvents.ExternalManned);
  const exDroneArray = createMVEvents(externalDrone, MonthViewEvents.ExternalDrone);
  const penArray = createMVEvents(pending, MonthViewEvents.PendingApproval);

  return [
    ...ppArray,
    ...conArray,
    ...droneArray,
    ...exMannedArray,
    ...exDroneArray,
    ...penArray
  ];
};

export const getMappedEvent = (list: any[]): IFlightsCalendarEvents[] => {
  return list.map((item: any) => {
    const sHour = Number(item.flightTime?.split(':')[0] ?? '');
    const sMinutes = Number(item.flightTime?.split(':')[1] ?? '');
    const eHour = Number(item.flightEndTime?.split(':')[0] ?? '');
    const eMinutes = Number(item.flightEndTime?.split(':')[1] ?? '');
    const start = Moment.utc(item.flightDate).hour(sHour).minutes(sMinutes)
      .toDate();
    const end = Moment.utc(item.flightDate).hour(eHour).minutes(eMinutes)
      .toDate();
    const alpha = end < new Date() ||
      item.requestStatus === RequestStatus.Cancelled ||
      item.requestStatus === RequestStatus.Declined ||
      item.requestStatus === RequestStatus.Completed ||
      item.requestStatus === RequestStatus.RequiresFlightCost ?
      0.5 : 1;
    const color = getColor(
      item.flightCategoryKey,
      item.requestStatus,
      alpha
    );
    const tarIcon = getTar(item.tarStatus);
    return {
      title: `${item.requestId} - ${item.flightCategory}`,
      start,
      end,
      backgroundColor: color,
      borderColor: 'black',
      textColor: `rgba(10, 10, 10, ${alpha})`,
      icon: getIcon(item.flightCategoryKey),
      categoryColor: getCategoryColor(item.flightCategoryKey, 1),
      flightTime: item.flightTime ?? '--',
      company: item.fieldCompany ?? '--',
      fieldName: item.fieldName ?? '--',
      tailNumber: item.tailNumber ?? '--',
      flightNumber: item.flightNo ?? '--',
      flightCategoryKey: item.flightCategoryKey,
      flightCategory: item.flightCategory,
      flightType: item.flightType ?? '--',
      operator: item.operatorName ?? '--',
      pilot: item.pilot ?? '--',
      location: item.location ?? '--',
      requestorDistrict: item.requestorDistrict,
      requestorId: item.requestId.toString(),
      requestorName: item.requestorName,
      requestStatus: item.requestStatus,
      tarStatus: item.tarStatus,
      tarIcon,
      hecIcon: item.flightType && item.flightType.includes('HEC') ? 'fa-solid fa-male' : '',
    };
  }) as IFlightsCalendarEvents[];
};

export const getEventContent = (args: EventContentArg): { html: string } => {
  const {
    end,
    icon,
    categoryColor,
    flightTime,
    company,
    tailNumber,
    flightNumber,
    flightCategory,
    flightType,
    operator,
    pilot,
    location,
    requestorDistrict,
    requestorName,
    requestStatus,
    tarStatus,
    tarIcon,
    hecIcon,
  } = args.event._def.extendedProps;
  const alpha = end < new Date() ||
      requestStatus === RequestStatus.Cancelled ||
      requestStatus === RequestStatus.Declined ?
      0.5 : 1;
  const miniBanner = `
    <div style="background-color: ${categoryColor}">
      <strong>${requestStatus?.toUpperCase()}</strong>
    </div>
  `;
  const padding = 'padding: 0.2em';
  const circularBorder = `
    border-radius: 10em;
    padding: 0.20em;
    border: 1px solid rgba(0, 0, 0, ${alpha});
    line-height: 1.25em;
  `;
  const dayView = `
    <div style="height: 500px;">
      ${requestStatus === RequestStatus.PendingApproval ||
        requestStatus === RequestStatus.Cancelled ||
        requestStatus === RequestStatus.Declined ? miniBanner : ''}
      <div style="${padding}">
        <div>
          <i class="${icon}" title="${flightCategory}" style="${circularBorder}"></i>
          <i class="${hecIcon}" title="HEC"></i>
          <i class="${tarIcon}" title="${
            tarStatus ? `${Lang.LBL_TAR} ${TARStatus[Number(tarStatus)]}` : Lang.LBL_TAR_REQUIRED
          }"></i>
          <strong>${args.event._def.title}</strong>
        </div>
        <div><strong>Actual: ${flightTime}</strong></div>
        <div>${company}</div>
        <div><strong>${tailNumber}</strong> - ${flightNumber}</div>
        <div>${operator} - ${pilot}</div>
        <div>${location}</div>
        <div>${requestorDistrict} / ${requestorName}</div>
        <div>${requestStatus}</div>
      </div>
    </div>
  `;
  const weekView = `
    ${requestStatus === RequestStatus.PendingApproval ||
      requestStatus === RequestStatus.Cancelled ||
      requestStatus === RequestStatus.Declined ? miniBanner : ''}
    <div style="${padding}">
      <div>
        <i class="${icon}" title="${flightCategory}" style="${circularBorder}"></i>
        <i class="${hecIcon}" title="HEC"></i>
        <i class="${tarIcon}" title="${
          tarStatus ? `${Lang.LBL_TAR} ${TARStatus[Number(tarStatus)]}` : Lang.LBL_TAR_REQUIRED
        }"></i>
      </div>
      <div><strong>${company}</strong></div>
      <div><strong>${tailNumber}</strong></div>
      <div>${flightType}</div>
      <div>${pilot}</div>
    </div>
  `;
  switch (args.view.type) {
    case CalendarView.DayView: {
      return { html: dayView };
    }
    case CalendarView.WeekView: {
      return { html: weekView };
    }
    default: {
      return { html: '' };
    }
  }
};

export const getMonthViewContent = (args: EventContentArg): { html: string } => {
  const {
    count,
    hecIcon,
    start,
    tarIcon,
  } = args.event._def.extendedProps;
  const padding = 'padding: 0.2em';
  const span1 = 'width: 60%;';
  const span2 = 'width: 40%; float: right;';
  const pendingStyle = (category, date) => `
    background-color: ${getCategoryBgColor(category, date)};
    padding: 0 0.15em;
    border: 1px solid #000000;
    text-align: center;
  `;
  const tar = tarIcon ?
    `<i class="${tarIcon}" title="${Lang.LBL_TAR} ${TARStatus[1]}"></i>` :
    '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;';
  const hec = hecIcon ?
    `<i class="${hecIcon}" title="HEC"></i>` :
    '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;';
  const pendingTitles = args.event._def.extendedProps.pendingCount ? {
    construction: `${MonthViewEvents.Construction} - ${args.event._def.extendedProps.pendingCount.construction}`,
    externalManned: `${MonthViewEvents.ExternalManned} - ${args.event._def.extendedProps.pendingCount.externalManned}`,
    externalDrone: `${MonthViewEvents.ExternalDrone} - ${args.event._def.extendedProps.pendingCount.externalDrone}`,
    drone: `${MonthViewEvents.InternalDrone} - ${args.event._def.extendedProps.pendingCount.drone}`,
    patrolPassenger: `${MonthViewEvents.PatrolPassenger} - ${args.event._def.extendedProps.pendingCount.patrolPassenger}`,
  } : {
    construction: '',
    externalManned: '',
    externalDrone: '',
    drone: '',
    patrolPassenger: '',
  };
  const countAndIcons = args.event._def.extendedProps.pendingCount ?
    `
      ${
        Number(args.event._def.extendedProps.pendingCount.construction) +
        Number(args.event._def.extendedProps.pendingCount.externalManned) +
        Number(args.event._def.extendedProps.pendingCount.externalDrone) +
        Number(args.event._def.extendedProps.pendingCount.drone) +
        Number(args.event._def.extendedProps.pendingCount.patrolPassenger)
      }
      <span style="${pendingStyle(MonthViewEvents.Construction, start)}" title="${pendingTitles.construction}">${args.event._def.extendedProps.pendingCount.construction}</span>
      <span style="${pendingStyle(MonthViewEvents.ExternalManned, start)}" title="${pendingTitles.externalManned}">${args.event._def.extendedProps.pendingCount.externalManned}</span>
      <span style="${pendingStyle(MonthViewEvents.ExternalDrone, start)}" title="${pendingTitles.externalDrone}">${args.event._def.extendedProps.pendingCount.externalDrone}</span>
      <span style="${pendingStyle(MonthViewEvents.InternalDrone, start)}" title="${pendingTitles.drone}">${args.event._def.extendedProps.pendingCount.drone}</span>
      <span style="${pendingStyle(MonthViewEvents.PatrolPassenger, start)}" title="${pendingTitles.patrolPassenger}">${args.event._def.extendedProps.pendingCount.patrolPassenger}</span>
    ` : `
      ${count}
      &nbsp;
      ${tar}
      ${hec}
    `;
  return { html: `
    <div style="${padding}">
      <span style="${span1}">
        <strong>${args.event._def.title}</strong>
      </span>
      <span style="${span2}">
        ${countAndIcons}
      </span>
    </div>
  ` };
};

export const getEventPopup = (args: EventContentArg): string => {
  const {
    icon,
    flightTime,
    fieldName,
    tailNumber,
    flightNumber,
    flightType,
    operator,
    pilot,
    location,
    requestorDistrict,
    requestorName,
    requestStatus,
  } = args.event._def.extendedProps;

  const circularBorder = `
    border-radius: 10em;
    padding: 0.25em;
    border: 1px solid black;
  `;

  return `
    <div>
      <div>
        <i class="${icon}" style="${circularBorder}"></i>
        ${flightType}
      </div>
      <div>
        <i class="fa-regular fa-clock"></i>
        Actual: ${flightTime}
      </div>
      <div>
        <i class="fa-solid fa-id-card"></i>
        ${fieldName}
      </div>
      <div>
        <i class="fa-solid fa-helicopter"></i>
        ${tailNumber} - ${flightNumber}
      </div>
      <div>
        <i class="fa-solid fa-user-pilot-tie"></i>
        ${operator} - ${pilot}
      </div>
      <div>
        <i class="fa-solid fa-map-marker-alt"></i>
        ${location}
      </div>
      <div>
        <i class="fa-regular fa-user"></i>
        ${requestorDistrict} - ${requestorName}
      </div>
      <div>
        <i class="fa-regular fa-calendar-alt"></i>
        ${requestStatus}
      </div>
    </div>
  `;
};
